import { useState } from "react";
import { Tooltip } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { useTranslation } from "@repo/core";
import type { PerformanceDetails } from "../../../types/strategy-detail";
import LoadingPerformance from "./LoadingPerformance";

interface PerformanceDetailsProps {
  performance: PerformanceDetails | null;
  isLoading?: boolean;
  className?: string;
  benchmarkName?: string;
}

/**
 * Performance details component showing advanced performance metrics table
 */
export const PerformanceDetailsComponent = ({
  performance,
  isLoading = false,
  className = "",
  benchmarkName = "SP500",
}: PerformanceDetailsProps) => {
  const [isAdvancedView, setIsAdvancedView] = useState(false);
  const { tStrategy, tCommon, tTable } = useTranslation();

  const toggleAdvancedView = () => setIsAdvancedView(!isAdvancedView);

  // Tooltip content mapping
  const tooltipContent = {
    strategy: tStrategy("tooltips.strategy"),
    benchmarkSP500: tStrategy("tooltips.benchmarkSP500"),
    universe: tStrategy("tooltips.universe"),
    longLegPerformance: tStrategy("tooltips.longLegPerformance"),
    shortLegPerformance: tStrategy("tooltips.shortLegPerformance"),
    longShortPerformance: tStrategy("tooltips.longShortPerformance"),
  };

  // Helper component for table headers with tooltip
  const HeaderWithTooltip = ({
    text,
    tooltipKey,
  }: {
    text: string;
    tooltipKey?: keyof typeof tooltipContent;
  }) => {
    if (!tooltipKey || !tooltipContent[tooltipKey]) {
      return <span>{text}</span>;
    }

    return (
      <div className="flex items-center gap-1">
        <span>{text}</span>
        <div className="relative z-50">
          <Tooltip content={tooltipContent[tooltipKey]} placement="top">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
      </div>
    );
  };

  // Helper component for metric names with optional description tooltip
  const customTheme = {
    target: "justify-end",
  };
  const MetricWithTooltip = ({
    name,
    description,
  }: {
    name: string;
    description?: string;
  }) => {
    if (!description) {
      return <span>{name}</span>;
    }

    return (
      <div className="flex items-end gap-1">
        <span>{name}</span>
        <div className="relative z-50">
          <Tooltip content={description} placement="right">
            <HiInformationCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </Tooltip>
        </div>
      </div>
    );
  };

  // Helper component for clickable cells with tooltip showing full value
  const ClickableCell = ({
    displayValue,
    tooltipValue,
  }: {
    displayValue: string;
    tooltipValue: string;
  }) => {
    return (
      <div className="flex justify-end">
        <Tooltip theme={customTheme} content={tooltipValue} placement="auto">
          <span className="cursor-pointer hover:text-blue-600 transition-colors underline decoration-dotted hover:decoration-solid w-full">
            {displayValue}
          </span>
        </Tooltip>
      </div>
    );
  };

  if (isLoading) {
    return <LoadingPerformance className={className} />;
  }

  if (!performance) {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {tStrategy("performanceDetailsTitle")}
        </h2>

        <hr className="border-gray-200 mb-6" />

        <div className="text-center py-12">
          <div className="text-gray-500">
            <div className="text-6xl mb-4">📈</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {tStrategy("performanceDataNotAvailable")}
            </h3>
            <p className="text-gray-600">
              {tStrategy("unableToLoadPerformanceDetails")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {tStrategy("performanceDetailsTitle")}
        </h2>
        <button
          onClick={toggleAdvancedView}
          className="text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium underline"
        >
          {isAdvancedView ? tCommon("basicView") : tCommon("advancedView")}
        </button>
      </div>

      {/* Horizontal divider */}
      <hr className="border-gray-200 mb-6" />

      {/* Performance Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="sticky left-0 bg-gray-50 z-20 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                {tTable("metrics")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {tTable("strategy")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <HeaderWithTooltip
                  text={tTable("benchmarkColumn", { name: benchmarkName })}
                  tooltipKey="benchmarkSP500"
                />
              </th>
              {isAdvancedView && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <HeaderWithTooltip
                      text={tTable("universe")}
                      tooltipKey="universe"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <HeaderWithTooltip
                      text={tTable("longLegPerformance")}
                      tooltipKey="longLegPerformance"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <HeaderWithTooltip
                      text={tTable("shortLegPerformance")}
                      tooltipKey="shortLegPerformance"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <HeaderWithTooltip
                      text={tTable("longShortPerformance")}
                      tooltipKey="longShortPerformance"
                    />
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {performance.metrics.map((metric, index) => (
              <tr
                key={metric.name}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="sticky left-0 bg-inherit z-20 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                  <MetricWithTooltip
                    name={metric.name}
                    description={metric.description}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <ClickableCell
                    displayValue={metric.strategy !== undefined ? metric.displayConversion(metric.strategy) : "—"}
                    tooltipValue={metric.strategy !== undefined ? metric.tooltipConversion(metric.strategy) : "—"}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <ClickableCell
                    displayValue={metric.benchmark !== undefined ? metric.displayConversion(metric.benchmark) : "—"}
                    tooltipValue={metric.benchmark !== undefined ? metric.tooltipConversion(metric.benchmark) : "—"}
                  />
                </td>
                {isAdvancedView && (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ClickableCell
                        displayValue={metric.universe !== undefined ? metric.displayConversion(metric.universe) : "—"}
                        tooltipValue={metric.universe !== undefined ? metric.tooltipConversion(metric.universe) : "—"}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ClickableCell
                        displayValue={metric.longLeg !== undefined ? metric.displayConversion(metric.longLeg) : "—"}
                        tooltipValue={metric.longLeg !== undefined ? metric.tooltipConversion(metric.longLeg) : "—"}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ClickableCell
                        displayValue={metric.shortLeg !== undefined ? metric.displayConversion(metric.shortLeg) : "—"}
                        tooltipValue={metric.shortLeg !== undefined ? metric.tooltipConversion(metric.shortLeg) : "—"}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ClickableCell
                        displayValue={metric.shortShort !== undefined ? metric.displayConversion(metric.shortShort) : "—"}
                        tooltipValue={metric.shortShort !== undefined ? metric.tooltipConversion(metric.shortShort) : "—"}
                      />
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceDetailsComponent;
